import { QueryBooleanOption, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { HttpStatusCode, PejHttpError } from '@common-lib';

import { useRoot } from '@frontend/utils';

export interface UseQueryClientProps<T> {
  onFetch: () => Promise<T>;
  onError?: (error: Error) => void;
  enabled?: QueryBooleanOption<T, Error, T, string[]> | undefined;
  refetchInterval?: number;
  queryKey: string[];
}

export const useClientQuery = <T>({
  onFetch: fetch,
  onError,
  enabled,
  queryKey,
  refetchInterval,
}: UseQueryClientProps<T>) => {
  const { removeLocalSession } = useRoot();

  const state = useQuery({
    queryKey: queryKey,
    queryFn: async () => await fetch(),
    enabled,
    refetchInterval,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: reacts once per error. onError and removeLocalSession are callbacks that can change identity every render, which would repeat them for the same error.
  useEffect(() => {
    if (state.isError) {
      if (state.error instanceof PejHttpError) {
        if (
          state.error.statusCode === HttpStatusCode.UNAUTHORIZED ||
          state.error.statusCode === HttpStatusCode.LOCKED
        ) {
          removeLocalSession();
        }
      }

      if (state.error instanceof Error) {
        onError?.(state.error);
      }
    }
  }, [state.isError, state.error]);

  return state;
};
